import { useAppSelector } from '@redux/hooks';
import React, { createContext } from 'react';
import { useCategories } from './hooks/useCategories';
import { useVendors } from './hooks/useVendors';
import type {
	CategoryRecommendationsContextType,
	CategoryRecommendationsProviderProps,
} from './types';

const defaultContext: CategoryRecommendationsContextType = {
	categories: [],
	selectedCategoryCode: null,
	vendorsByCategory: {},
	isLoading: false,
	error: null,
	onSelectCategory: () => {},
	shouldShowNoResults: false,
	selectedCategoryVendors: [],
};

export const CategoryRecommendationsContext =
	createContext<CategoryRecommendationsContextType>(defaultContext);

export const CategoryRecommendationsProvider = ({
	vendorId,
	memberId,
	anonymousId,
	excludeCategoryCode,
	marketCode,
	children,
}: CategoryRecommendationsProviderProps) => {
	const {
		categories,
		selectedCategoryCode,
		setSelectedCategoryCode,
		isLoading: categoriesLoading,
		error: categoriesError,
	} = useCategories(memberId, anonymousId, excludeCategoryCode, marketCode);

	const currentMemberId = useAppSelector(
		(state) => state.membership.member?.id || '',
	);
	const conversations = useAppSelector(
		(state) => state.messagedVendors.conversations,
	);

	const {
		vendorsByCategory,
		selectedCategoryVendors,
		isLoading: vendorsLoading,
		error: vendorsError,
	} = useVendors(
		vendorId,
		selectedCategoryCode,
		currentMemberId,
		conversations,
	);

	const onSelectCategory = (category: Category.CategoryCode) => {
		setSelectedCategoryCode(category);
	};

	const shouldShowNoResults =
		selectedCategoryVendors.length === 0 &&
		!categoriesLoading &&
		!vendorsLoading &&
		categories.length > 0;

	const isLoading = categoriesLoading || vendorsLoading;
	const error = categoriesError || vendorsError;

	const value: CategoryRecommendationsContextType = {
		categories,
		selectedCategoryCode,
		vendorsByCategory,
		isLoading,
		error,
		onSelectCategory,
		shouldShowNoResults,
		selectedCategoryVendors,
	};

	return (
		<CategoryRecommendationsContext.Provider value={value}>
			{children}
		</CategoryRecommendationsContext.Provider>
	);
};
